/* Module Comptabilité — préparation comptable
 *
 * 7 sous-onglets : Tableau de bord, Exports, Justificatifs, Dossiers, TVA,
 * Historique, Paramètres.
 * Mobile-first responsive. Ne remplace aucun module existant.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard, Download, Files, FolderArchive, Percent, History,
  Settings2, Loader2, CheckCircle2, XCircle, AlertTriangle, Send, Mail,
  FileSpreadsheet, FileText, Package, ExternalLink, Sparkles, Info, Shield,
  RefreshCw,
} from "lucide-react";

/* Fallback réutilisable affiché quand un onglet Comptabilité ne peut charger
 * une donnée du backend (502, 503, timeout, réseau). Évite l'écran rouge Axios
 * en affichant un message utilisateur et un bouton de nouvel essai. */
function InlineServiceError({ onRetry }) {
  return (
    <div className="card-elevated p-8 text-center" data-testid="accounting-inline-error">
      <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center">
        <AlertTriangle className="h-6 w-6 text-amber-600" />
      </div>
      <div className="font-heading text-lg">Service momentanément indisponible</div>
      <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
        Le service est momentanément indisponible. Réessayez dans quelques instants.
      </p>
      {onRetry && (
        <Button className="mt-4" onClick={onRetry} data-testid="accounting-inline-retry">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Réessayer
        </Button>
      )}
    </div>
  );
}

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
const pct = (v) => `${Number(v || 0).toFixed(1)} %`;

const RECIPIENT_LABEL = {
  expert_comptable: "Expert-comptable", banque: "Banque",
  associe: "Associé", investisseur: "Investisseur",
};

/* =========================================================================
   Page principale
   ========================================================================= */

export default function AccountingPage() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="animate-fade-up" data-testid="accounting-page">
      <PageHeader
        overline="Comptabilité"
        title="Préparation comptable"
        description="Toutes vos pièces comptables consolidées en un endroit. Aucune ressaisie : les données du patrimoine, des loyers et des crédits alimentent automatiquement vos exports et dossiers."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-4 md:mx-0 mb-6 overflow-x-auto scrollbar-none">
          <TabsList className="w-max md:w-auto px-4 md:px-0 h-auto flex-nowrap">
            <TabsTrigger value="dashboard" data-testid="acc-tab-dashboard">
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />Tableau de bord
            </TabsTrigger>
            <TabsTrigger value="exports" data-testid="acc-tab-exports">
              <Download className="h-3.5 w-3.5 mr-1.5" />Exports
            </TabsTrigger>
            <TabsTrigger value="justificatifs" data-testid="acc-tab-justificatifs">
              <Files className="h-3.5 w-3.5 mr-1.5" />Justificatifs
            </TabsTrigger>
            <TabsTrigger value="dossiers" data-testid="acc-tab-dossiers">
              <FolderArchive className="h-3.5 w-3.5 mr-1.5" />Dossiers
            </TabsTrigger>
            <TabsTrigger value="vat" data-testid="acc-tab-vat">
              <Percent className="h-3.5 w-3.5 mr-1.5" />TVA
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="acc-tab-history">
              <History className="h-3.5 w-3.5 mr-1.5" />Historique
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="acc-tab-settings">
              <Settings2 className="h-3.5 w-3.5 mr-1.5" />Paramètres
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="exports"><ExportsTab /></TabsContent>
        <TabsContent value="justificatifs"><JustificatifsTab /></TabsContent>
        <TabsContent value="dossiers"><DossiersTab /></TabsContent>
        <TabsContent value="vat"><VatTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =========================================================================
   Composants partagés
   ========================================================================= */

function FiltersBar({ filters, setFilters }) {
  const [links, setLinks] = useState({ companies: [], buildings: [] });
  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/buildings")]).then(([c, b]) =>
      setLinks({ companies: c.data.items || [], buildings: b.data.items || [] }));
  }, []);
  return (
    <div className="card-elevated p-4 flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[160px]">
        <Label className="text-xs">Société</Label>
        <Select value={filters.company_id || "__all"}
                onValueChange={(v) => setFilters({ ...filters, company_id: v === "__all" ? "" : v, building_id: "" })}>
          <SelectTrigger data-testid="acc-filter-company"><SelectValue placeholder="Toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Toutes</SelectItem>
            {links.companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 min-w-[160px]">
        <Label className="text-xs">Immeuble</Label>
        <Select value={filters.building_id || "__all"}
                onValueChange={(v) => setFilters({ ...filters, building_id: v === "__all" ? "" : v })}>
          <SelectTrigger data-testid="acc-filter-building"><SelectValue placeholder="Tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Tous</SelectItem>
            {links.buildings.filter((b) => !filters.company_id || b.company_id === filters.company_id)
              .map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[130px]">
        <Label className="text-xs">Début</Label>
        <Input type="date" value={filters.period_start || ""}
               onChange={(e) => setFilters({ ...filters, period_start: e.target.value })}
               data-testid="acc-filter-start" />
      </div>
      <div className="min-w-[130px]">
        <Label className="text-xs">Fin</Label>
        <Input type="date" value={filters.period_end || ""}
               onChange={(e) => setFilters({ ...filters, period_end: e.target.value })}
               data-testid="acc-filter-end" />
      </div>
    </div>
  );
}

/* =========================================================================
   TAB 1 — Tableau de bord
   ========================================================================= */

function DashboardTab() {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoadError(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    try {
      const [d, c] = await Promise.all([
        api.get("/accounting/dashboard", { params }),
        api.get("/accounting/completeness", { params }),
      ]);
      setData(d.data); setComp(c.data);
    } catch (err) {
      console.error("Accounting dashboard load failed:", err);
      setLoadError(err);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  if (loadError) return <InlineServiceError onRetry={load} />;
  if (!data || !comp) return <div className="text-muted-foreground">Chargement…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6">
      <FiltersBar filters={filters} setFilters={setFilters} />

      {/* Score complétude */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-elevated p-6 text-center flex flex-col justify-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Complétude comptable</div>
          <div className="mt-3 flex items-baseline justify-center gap-2">
            <div className="text-6xl font-bold text-primary" data-testid="acc-score">{comp.score}</div>
            <div className="text-xl text-muted-foreground">%</div>
          </div>
          <div className="mt-2">
            <StatusBadge tone={comp.score >= 75 ? "success" : comp.score >= 60 ? "warning" : "danger"}>
              Grade {comp.grade}
            </StatusBadge>
          </div>
        </div>
        <div className="card-elevated p-6 col-span-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Décomposition</div>
          <div className="space-y-2">
            {Object.entries(comp.breakdown).map(([k, v]) => (
              <div key={k} className="space-y-1">
                <div className="flex justify-between text-sm capitalize">
                  <span>{k}</span><span className="font-mono">{v} / 100</span>
                </div>
                <div className="h-2 bg-secondary rounded overflow-hidden">
                  <div className={`h-full ${v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                       style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KPI Pièces */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Pièces comptables</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="acc-total" label="Total pièces" value={data.counts.pieces_total} tone="accent" />
          <KpiCard name="acc-charges" label="Factures / charges" value={data.counts.pieces_charges} />
          <KpiCard name="acc-rents" label="Loyers" value={data.counts.pieces_rents} />
          <KpiCard name="acc-credits" label="Crédits" value={data.counts.pieces_credits} />
          <KpiCard name="acc-missing" label="Manquants" value={data.counts.missing_total}
                    tone={data.counts.missing_total > 0 ? "warning" : "success"} />
        </div>
      </section>

      {/* KPI Financier */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Financier</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard name="acc-rents-paid" label="Loyers encaissés" value={eur(t.rents_paid)} tone="accent" />
          <KpiCard name="acc-rents-pending" label="Loyers restants" value={eur(t.rents_pending)} />
          <KpiCard name="acc-charges-total" label="Charges" value={eur(t.charges_total)} />
          <KpiCard name="acc-travaux" label="Travaux" value={eur(t.travaux_total)} />
          <KpiCard name="acc-assur" label="Assurances" value={eur(t.assurances_total)} />
          <KpiCard name="acc-credits-total" label="Crédits" value={eur(t.credits_total)} />
          <KpiCard name="acc-interets" label="Intérêts" value={eur(t.interests_total)} />
          <KpiCard name="acc-tf" label="Taxe foncière" value={eur(t.taxe_fonciere_total)} />
          <KpiCard name="acc-honoraires" label="Honoraires" value={eur(t.honoraires_total)} />
          <KpiCard name="acc-frais-banc" label="Frais bancaires" value={eur(t.frais_bancaires_total)} />
        </div>
      </section>

      {/* KPI TVA + Résultat */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">TVA / Résultat</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard name="acc-tva-ded" label="TVA déductible" value={eur(t.vat_deductible)} />
          <KpiCard name="acc-tva-col" label="TVA collectée" value={eur(t.vat_collected)} />
          <KpiCard name="acc-tva-bal" label="Solde TVA" value={eur(t.vat_balance)} />
          <KpiCard name="acc-result" label="Résultat estimatif" value={eur(t.result_estimate)}
                    tone={t.result_estimate >= 0 ? "success" : "danger"} />
        </div>
      </section>

      {/* Dépenses par catégorie */}
      {data.expenses_by_category.length > 0 && (
        <section className="card-elevated p-5">
          <div className="font-heading text-base mb-3">Dépenses par catégorie</div>
          <div className="space-y-2">
            {data.expenses_by_category.slice(0, 10).map((c) => (
              <div key={c.code} className="flex items-center gap-3 text-sm">
                <span className="w-40 truncate">{c.label}</span>
                <div className="flex-1 h-2 bg-secondary rounded overflow-hidden">
                  <div className="h-full bg-primary"
                       style={{ width: `${(c.total / Math.max(...data.expenses_by_category.map((x) => x.total || 1))) * 100}%` }} />
                </div>
                <span className="w-28 text-right font-mono">{eur(c.total)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 2 — Exports
   ========================================================================= */

function ExportsTab() {
  const [meta, setMeta] = useState({ categories: [], connectors: [] });
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState("csv");
  const [connector, setConnector] = useState("csv_generic");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/accounting/meta")
      .then(({ data }) => setMeta(data))
      .catch((err) => console.error("Load accounting meta failed:", err));
  }, []);

  const toggle = (code) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/exports`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          categories: Array.from(selected),
          format, connector,
          ...filters,
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `comptabilite_${format === "xlsx" ? "xlsx" : format}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) { toast.error(String(e).slice(0, 200)); }
    finally { setBusy(false); }
  };

  const availableConnectors = meta.connectors.filter((c) => c.status === "available");
  const roadmapConnectors = meta.connectors.filter((c) => c.status === "roadmap");

  return (
    <div className="space-y-5">
      <FiltersBar filters={filters} setFilters={setFilters} />

      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base">Catégories à exporter</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {meta.categories.map((c) => (
            <label key={c.code} className="flex items-start gap-2 p-3 rounded-lg border hover:bg-secondary/40 cursor-pointer"
                    data-testid={`exp-cat-${c.code}`}>
              <input type="checkbox"
                     checked={selected.has(c.code)}
                     onChange={() => toggle(c.code)}
                     className="mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-medium">{c.label}</div>
                <div className="text-xs text-muted-foreground">{c.kind}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          Aucune catégorie cochée = toutes les catégories exportées.
        </div>
      </div>

      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base">Format et connecteur</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger data-testid="exp-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Connecteur</Label>
            <Select value={connector} onValueChange={setConnector}>
              <SelectTrigger data-testid="exp-connector"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableConnectors.map((c) =>
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={doExport} disabled={busy} data-testid="exp-run">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
          Exporter
        </Button>
      </div>

      {roadmapConnectors.length > 0 && (
        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center gap-2 text-sm text-primary/90 mb-2">
            <Sparkles className="h-3.5 w-3.5" /><strong>Connecteurs à venir</strong>
          </div>
          <div className="text-xs text-muted-foreground mb-3">
            L&apos;architecture est prête. Ces connecteurs pourront être activés sans modification du code existant.
          </div>
          <div className="flex flex-wrap gap-2">
            {roadmapConnectors.map((c) => (
              <StatusBadge key={c.code} tone="neutral">{c.label}</StatusBadge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 3 — Justificatifs
   ========================================================================= */

function JustificatifsTab() {
  const [filters, setFilters] = useState({});
  const [comp, setComp] = useState(null);
  const [error, setError] = useState(null);
  const reload = () => {
    setError(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/accounting/completeness", { params })
      .then(({ data }) => setComp(data))
      .catch((err) => { console.error("Load completeness failed:", err); setError(err); });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  if (error) return <InlineServiceError onRetry={reload} />;
  if (!comp) return <div className="text-muted-foreground">Chargement…</div>;

  const sections = [
    { title: "Justificatifs manquants", key: "missing_justificatifs",
      count: comp.issues.missing_justificatifs.length,
      icon: <AlertTriangle className="h-4 w-4 text-amber-500" /> },
    { title: "Charges non affectées", key: "unassigned",
      count: comp.issues.unassigned.length,
      icon: <AlertTriangle className="h-4 w-4 text-amber-500" /> },
    { title: "Sans catégorie", key: "no_category",
      count: comp.issues.no_category.length,
      icon: <Info className="h-4 w-4 text-blue-500" /> },
    { title: "Détails incomplets", key: "incomplete",
      count: comp.issues.incomplete.length,
      icon: <Info className="h-4 w-4 text-blue-500" /> },
    { title: "TVA à vérifier", key: "vat_to_check",
      count: comp.issues.vat_to_check.length,
      icon: <Percent className="h-4 w-4 text-primary" /> },
  ];

  return (
    <div className="space-y-4">
      <FiltersBar filters={filters} setFilters={setFilters} />
      {sections.map((sec) => (
        <div key={sec.key} className="card-elevated" data-testid={`just-section-${sec.key}`}>
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2 font-heading text-sm">
              {sec.icon} {sec.title}
            </div>
            <StatusBadge tone={sec.count > 0 ? "warning" : "success"}>{sec.count}</StatusBadge>
          </div>
          {sec.count === 0 ? (
            <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />Rien à signaler.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-2">Libellé</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2 text-right">Montant</th>
                    {sec.key === "incomplete" && <th className="px-4 py-2">Champs manquants</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(comp.issues[sec.key] || []).slice(0, 30).map((item, i) => (
                    <tr key={item.id || i}>
                      <td className="px-4 py-2">{item.label || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs">{item.date || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono">{eur(item.amount)}</td>
                      {sec.key === "incomplete" && (
                        <td className="px-4 py-2 text-xs text-red-600">{(item.missing_fields || []).join(", ")}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      <div className="text-xs text-muted-foreground text-center">
        Loyers impayés détectés : {comp.issues.rents_unpaid_count || 0}
      </div>
    </div>
  );
}

/* =========================================================================
   TAB 4 — Dossiers comptables (1 clic)
   ========================================================================= */

function DossiersTab() {
  const [items, setItems] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    period_start: "", period_end: "", label: "",
    include: ["exports", "justificatifs", "credits", "documents_ocr", "echeanciers"],
  });
  const [openSend, setOpenSend] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get("/accounting/dossiers");
      setItems(data.items);
    } catch (err) {
      console.error("Load dossiers failed:", err);
      setError(err);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.period_start || !form.period_end) {
      toast.error("Indiquez une période"); return;
    }
    try {
      const { data } = await api.post("/accounting/dossiers", form);
      toast.success(`Dossier prêt : ${data.files_included.reduce((s, x) => s + x.count, 0)} pièces`);
      setOpenNew(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const download = async (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/dossiers/${id}/download`;
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) { toast.error("Téléchargement impossible"); return; }
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dossier_${id.slice(0, 6)}.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce dossier ?")) return;
    await api.delete(`/accounting/dossiers/${id}`);
    load();
  };

  const toggleInclude = (key) => {
    setForm((f) => ({
      ...f,
      include: f.include.includes(key)
        ? f.include.filter((x) => x !== key)
        : [...f.include, key],
    }));
  };

  const INCLUDE_LABELS = {
    exports: "Exports CSV/Excel/PDF",
    justificatifs: "Factures / justificatifs",
    factures: "Factures",
    releves: "Relevés",
    echeanciers: "Échéanciers de crédits",
    credits: "Crédits",
    assurances: "Assurances",
    taxes: "Taxes",
    documents_ocr: "Documents validés par IA",
  };

  if (error) return <InlineServiceError onRetry={load} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="dossier-new-btn">
              <Package className="h-3 w-3 mr-1" />Nouveau dossier (1 clic)
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Préparer un dossier comptable</DialogTitle>
              <DialogDescription>
                Sélectionnez une période et les sections à inclure. Fonbex génère un ZIP prêt à envoyer à votre expert-comptable.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Début *</Label>
                  <Input type="date" value={form.period_start}
                         onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                         data-testid="dossier-start" />
                </div>
                <div>
                  <Label>Fin *</Label>
                  <Input type="date" value={form.period_end}
                         onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                         data-testid="dossier-end" />
                </div>
              </div>
              <div>
                <Label>Libellé (facultatif)</Label>
                <Input value={form.label}
                       onChange={(e) => setForm({ ...form, label: e.target.value })}
                       placeholder="Ex : Exercice 2025 SCI Immobilis" />
              </div>
              <div>
                <Label>Sections à inclure</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {Object.entries(INCLUDE_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"
                            data-testid={`dossier-inc-${k}`}>
                      <input type="checkbox" checked={form.include.includes(k)}
                             onChange={() => toggleInclude(k)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenNew(false)}>Annuler</Button>
              <Button onClick={create} data-testid="dossier-create">
                <Package className="h-3 w-3 mr-1" />Générer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 && (
        <div className="card-elevated p-12 text-center text-muted-foreground">
          <FolderArchive className="mx-auto h-8 w-8 mb-3 opacity-60" />
          Aucun dossier préparé. Cliquez sur « Nouveau dossier » pour démarrer.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((d) => (
          <div key={d.id} className="card-elevated p-5" data-testid={`dossier-card-${d.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="font-heading text-base">{d.label}</div>
                <div className="text-xs text-muted-foreground">
                  {d.period_start} → {d.period_end}
                </div>
              </div>
              <StatusBadge tone="success">{d.status}</StatusBadge>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              {d.line_count} lignes · {Math.round(d.size_bytes / 1024)} Ko · {new Date(d.created_at).toLocaleDateString("fr-FR")}
            </div>
            <div className="mt-2 text-xs">
              {(d.files_included || []).map((f) => (
                <span key={f.kind} className="mr-2">
                  <span className="text-muted-foreground">{f.kind}:</span>{" "}
                  <span className="font-medium">{f.count}</span>
                </span>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => download(d.id)}
                       data-testid={`dossier-dl-${d.id}`}>
                <Download className="h-3 w-3 mr-1" />ZIP
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpenSend(d)}
                       data-testid={`dossier-send-${d.id}`}>
                <Send className="h-3 w-3 mr-1" />Envoyer
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(d.id)}
                       data-testid={`dossier-rm-${d.id}`}>
                <XCircle className="h-3 w-3 mr-1" />Supprimer
              </Button>
            </div>
          </div>
        ))}
      </div>

      {openSend && <SendDossierDialog dossier={openSend} onClose={() => { setOpenSend(null); load(); }} />}
    </div>
  );
}

function SendDossierDialog({ dossier, onClose }) {
  const [form, setForm] = useState({
    recipient_email: "", recipient_role: "expert_comptable",
    subject: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.recipient_email) { toast.error("Adresse email requise"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/accounting/sendings", {
        dossier_id: dossier.id,
        ...form,
      });
      if (data.status === "logged") {
        toast.info("Email en mode dev (RESEND_API_KEY absente) — enregistré au journal");
      } else if (data.status === "sent") {
        toast.success("Email envoyé");
      } else {
        toast.error(`Envoi en erreur (${data.status})`);
      }
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Envoyer ce dossier par email</DialogTitle>
          <DialogDescription>
            Le destinataire recevra un email avec le résumé du dossier. La sécurité passe par le journal Fonbex.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Rôle du destinataire</Label>
            <Select value={form.recipient_role}
                    onValueChange={(v) => setForm({ ...form, recipient_role: v })}>
              <SelectTrigger data-testid="send-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(RECIPIENT_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Adresse email *</Label>
            <Input type="email" value={form.recipient_email}
                   onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                   placeholder="expert@cabinet.fr" data-testid="send-email" />
          </div>
          <div>
            <Label>Sujet (facultatif)</Label>
            <Input value={form.subject}
                   onChange={(e) => setForm({ ...form, subject: e.target.value })}
                   placeholder="Sujet par défaut si vide" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea rows={4} value={form.message}
                       onChange={(e) => setForm({ ...form, message: e.target.value })}
                       placeholder="Message d&apos;accompagnement" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={sending} data-testid="send-submit">
            {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   TAB 5 — TVA
   ========================================================================= */

function VatTab() {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = () => {
    setError(null);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/accounting/vat", { params })
      .then(({ data }) => setData(data))
      .catch((err) => { console.error("Load VAT failed:", err); setError(err); });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);
  if (error) return <InlineServiceError onRetry={reload} />;
  if (!data) return <div className="text-muted-foreground">Chargement…</div>;
  return (
    <div className="space-y-5">
      <FiltersBar filters={filters} setFilters={setFilters} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard name="vat-ded" label="TVA déductible" value={eur(data.totals.vat_deductible)} />
        <KpiCard name="vat-col" label="TVA collectée" value={eur(data.totals.vat_collected)} />
        <KpiCard name="vat-bal" label="Solde TVA (à payer si +)" value={eur(data.totals.vat_balance)}
                  tone={data.totals.vat_balance > 0 ? "warning" : "success"} />
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left"><th className="px-4 py-2">Taux</th><th className="px-4 py-2 text-right">Base HT</th><th className="px-4 py-2 text-right">TVA</th><th className="px-4 py-2 text-right">Pièces</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.by_rate.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">Aucune TVA détectée.</td></tr>
            ) : data.by_rate.map((r, i) => (
              <tr key={`vat-${r.rate}-${i}`}>
                <td className="px-4 py-2 font-mono">{r.rate} %</td>
                <td className="px-4 py-2 text-right font-mono">{eur(r.ht)}</td>
                <td className="px-4 py-2 text-right font-mono">{eur(r.vat)}</td>
                <td className="px-4 py-2 text-right">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3 text-xs text-primary/80">
        {data.note}
      </div>
    </div>
  );
}

/* =========================================================================
   TAB 6 — Historique (journal des actions comptables + envois)
   ========================================================================= */

function HistoryTab() {
  const [items, setItems] = useState([]);
  const [sendings, setSendings] = useState([]);
  const [tab, setTab] = useState("journal");
  const [error, setError] = useState(null);
  const load = async () => {
    setError(null);
    try {
      const [j, s] = await Promise.all([
        api.get("/accounting/journal"),
        api.get("/accounting/sendings"),
      ]);
      setItems(j.data.items); setSendings(s.data.items);
    } catch (err) {
      console.error("Load journal/sendings failed:", err);
      setError(err);
    }
  };
  useEffect(() => { load(); }, []);
  if (error) return <InlineServiceError onRetry={load} />;
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={tab === "journal" ? "default" : "outline"} onClick={() => setTab("journal")}
                data-testid="hist-view-journal">
          Journal ({items.length})
        </Button>
        <Button variant={tab === "sendings" ? "default" : "outline"} onClick={() => setTab("sendings")}
                data-testid="hist-view-sendings">
          Envois ({sendings.length})
        </Button>
      </div>
      {tab === "journal" && (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr className="text-left"><th className="px-4 py-2">Date</th><th className="px-4 py-2">Action</th><th className="px-4 py-2">Cible</th><th className="px-4 py-2">Détails</th></tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">Aucune activité comptable enregistrée.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2 font-mono text-xs">{new Date(it.created_at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2"><StatusBadge tone="info">{it.action}</StatusBadge></td>
                  <td className="px-4 py-2 font-mono text-xs">{it.target}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-md">{JSON.stringify(it.detail || {})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "sendings" && (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr className="text-left"><th className="px-4 py-2">Date</th><th className="px-4 py-2">Destinataire</th><th className="px-4 py-2">Rôle</th><th className="px-4 py-2">Sujet</th><th className="px-4 py-2">Statut</th></tr>
            </thead>
            <tbody className="divide-y">
              {sendings.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Aucun envoi.</td></tr>
              ) : sendings.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-2 font-mono text-xs">{new Date(s.created_at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2">{s.recipient_email}</td>
                  <td className="px-4 py-2 text-xs">{RECIPIENT_LABEL[s.recipient_role] || s.recipient_role}</td>
                  <td className="px-4 py-2 text-xs truncate max-w-xs">{s.subject}</td>
                  <td className="px-4 py-2"><StatusBadge tone={s.status === "sent" ? "success" : s.status === "logged" ? "info" : "danger"}>{s.status}</StatusBadge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   TAB 7 — Paramètres
   ========================================================================= */

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get("/accounting/settings");
      setSettings(data);
    } catch (err) {
      console.error("Load accounting settings failed:", err);
      setError(err);
    }
  };
  useEffect(() => { load(); }, []);
  if (error) return <InlineServiceError onRetry={load} />;
  if (!settings) return <div className="text-muted-foreground">Chargement…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      // Nettoyage : ne pas envoyer null explicites
      Object.keys(payload).forEach((k) => (payload[k] === null || payload[k] === "") && delete payload[k]);
      await api.patch("/accounting/settings", payload);
      toast.success("Paramètres enregistrés");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base">Destinataires par défaut</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Email expert-comptable</Label>
            <Input type="email" value={settings.default_expert_email || ""}
                   onChange={(e) => setSettings({ ...settings, default_expert_email: e.target.value })}
                   data-testid="set-expert-email" />
          </div>
          <div>
            <Label>Email destinataire alternatif</Label>
            <Input type="email" value={settings.default_recipient_email || ""}
                   onChange={(e) => setSettings({ ...settings, default_recipient_email: e.target.value })} />
          </div>
        </div>
      </div>
      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base flex items-center gap-2">
          <Percent className="h-4 w-4" />Régime fiscal
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Régime TVA</Label>
            <Select value={settings.vat_regime}
                    onValueChange={(v) => setSettings({ ...settings, vat_regime: v })}>
              <SelectTrigger data-testid="set-vat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="franchise">Franchise de TVA</SelectItem>
                <SelectItem value="reel_simplifie">Réel simplifié</SelectItem>
                <SelectItem value="reel_normal">Réel normal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Début exercice fiscal (mois)</Label>
            <Input type="number" min="1" max="12" value={settings.fiscal_year_start_month || 1}
                   onChange={(e) => setSettings({ ...settings, fiscal_year_start_month: Number(e.target.value) })} />
          </div>
        </div>
      </div>
      <div className="card-elevated p-5 space-y-4">
        <div className="font-heading text-base">Amortissements (préparation)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Immobilier (années)</Label>
            <Input type="number" value={settings.amortization_years_property || 25}
                   onChange={(e) => setSettings({ ...settings, amortization_years_property: Number(e.target.value) })}
                   data-testid="set-amort-property" />
          </div>
          <div>
            <Label>Travaux (années)</Label>
            <Input type="number" value={settings.amortization_years_works || 10}
                   onChange={(e) => setSettings({ ...settings, amortization_years_works: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Mobilier (années)</Label>
            <Input type="number" value={settings.amortization_years_furniture || 7}
                   onChange={(e) => setSettings({ ...settings, amortization_years_furniture: Number(e.target.value) })} />
          </div>
        </div>
      </div>
      <div className="card-elevated p-5 space-y-3">
        <div className="font-heading text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4" />Architecture ouverte
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={settings.enable_ocr_auto_ingest || false}
                 onChange={(e) => setSettings({ ...settings, enable_ocr_auto_ingest: e.target.checked })} />
          Activer l&apos;ingestion OCR automatique (préparation — module OCR à connecter)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={settings.enable_bank_reconciliation || false}
                 onChange={(e) => setSettings({ ...settings, enable_bank_reconciliation: e.target.checked })} />
          Activer le rapprochement bancaire (préparation — connecteur à venir)
        </label>
        <div className="text-xs text-muted-foreground pt-2">
          Ces options ne modifient encore rien à l&apos;exécution. Elles préparent l&apos;architecture des futurs modules.
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="set-save">
          {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
